import _L from "../../../../util/Labels";
import autoTable, { CellHookData, RowInput, UserOptions } from "jspdf-autotable";
import jsPDF from "jspdf";
import { UIUtilViewCalificacion } from "../../Calificacion";
import { Entidad } from "../../../../data/Entidad";

import IBoletaConfig = UIUtilViewCalificacion.IBoletaConfigV2;
import CTipoEvaluacion = Entidad.CTipoEvaluacion;
import ICalificacionInfoAPeriods = UIUtilViewCalificacion.ICalificacionInfoAPeriods;
import ItemConfigEval = UIUtilViewCalificacion.ItemConfigEval;
import { BoletaBase } from "./BoletaBase";
import { ascending } from "d3-array";

interface ISubjectsEvals {
    SubjectName: string;
    QualificationsData: (string | number)[][];
}

const usesInfoHeader = false;

class BoletaPDFGeneratorV4_1 extends BoletaBase {
    protected SortQualif(): void {
        const { Extras } = this.config.BoletaCfgTemplate
        if (!Extras?.materias_orden || !Extras?.materias_orden.length) return;
        const lowerMateriasOrder = Extras.materias_orden.map(d => d.toLowerCase());
        const materiasOrderMap = new Map<string, number>(lowerMateriasOrder.map((d, i) => [d, ++i]));
        const defaultOrder = materiasOrderMap.size + 1;

        this.config.MateriasCalificacionesAllPeriods
            .sort((a, b) => ascending((materiasOrderMap.get(a.MateriaNombre.toLowerCase()) || defaultOrder), (materiasOrderMap.get(b.MateriaNombre.toLowerCase()) || defaultOrder)))
            .sort((a, b) => ascending((a.TipoEval == CTipoEvaluacion.Numeros ? -1 : a.TipoEval), (b.TipoEval == CTipoEvaluacion.Numeros ? -1 : b.TipoEval)))
    }
    private usesFinalColumn: boolean = false;
    private usesStudentInfoExtra: boolean = false;

    constructor(config: IBoletaConfig) {
        super(config);
    }

    protected GetLogoWidth(): number {
        return this.auxDivWidth * 1.75;
    }

    protected DrawQualificationTbl(evalTypeSubjectsQualifs: ICalificacionInfoAPeriods[], evalType: CTipoEvaluacion, evalCfg: ItemConfigEval[] = []) {
        const { FontSizeShort, FontSizeMedium } = this.GetBasicPDFStyles();
        const TableStylesAux = this.GetBasicPDFTableStyles();
        const columnsHeaders: RowInput = [_L("boleta_v4.curr_organizer"), _L("boleta_v4.primer_trimestre"), _L("boleta_v4.segundo_trimestre"), _L("boleta_v4.tercer_trimestre")];
        if (this.usesFinalColumn) {
            columnsHeaders.push(_L("boleta_v4.final"));
        }

        let subjectCount = 0;

        const getColumnHeadAndStyles = (columnType: 'header' | 'body') => {
            const columnStyles: UserOptions["columnStyles"] = {};
            const columnsHead: Record<string, string> = {};

            columnsHeaders.forEach((_, i) => {
                if (i == 0) {
                    columnStyles[i] = { cellWidth: curricularOrganizerWidth * this.auxDivWidth, fontSize: columnType == 'body' ? FontSizeShort : FontSizeMedium, halign: columnType == 'body' ? "left" : 'center' };
                } else {
                    columnStyles[i] = { cellWidth: (i == 0 ? curricularOrganizerWidth : qualificatiosWidth) * this.auxDivWidth }
                }
                columnsHead[i] = "";
            })

            return {
                columnsHead,
                columnStyles,
            }
        }

        let curricularOrganizerWidth = 3.25;
        let qualificatiosWidth = (6 - curricularOrganizerWidth) / (3); // NOTE: 6 Es el número que nos ayuda a dividir el documento de forma imaginaria

        const subjectsQualifications = new Map<string, ISubjectsEvals>(evalTypeSubjectsQualifs.map((subject, i) => {
            const criteriosSubjectRowData = subject.Evaluaciones.map(criterioSubject => {
                return [
                    criterioSubject.Nombre,
                    ...criterioSubject.Evaluaciones.flatMap((d) => d[0] == '' ? "-" : d.join("\n")),
                ]
            });
            return [subject.MateriaNombre + i, <ISubjectsEvals>{ SubjectName: subject.MateriaNombre, QualificationsData: criteriosSubjectRowData }];
        }));

        if (evalType == CTipoEvaluacion.Colores || evalType == CTipoEvaluacion.Letras) {
            this.DrawEvaluationCfg(evalType, evalCfg);
        }

        const { columnsHead, columnStyles } = getColumnHeadAndStyles('header');
        const { columnsHead: colsHead, columnStyles: colsStyles } = getColumnHeadAndStyles('body');
        subjectsQualifications.forEach((subjectQualifs) => {
            subjectCount++;
            autoTable(this.pdf, {
                startY: this.pdfCoordinates.yPos,
                head: [{ "subject": `${this.RomanNumConversion(subjectCount)}. ${subjectQualifs.SubjectName}` }],
                styles: {
                    ...TableStylesAux,
                    halign: "center",
                    valign: "middle",
                },
                theme: "grid",
                margin: this.GetBasicMargins(),
            })
            this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;

            if (subjectCount == 1) {
                autoTable(this.pdf, {
                    startY: this.pdfCoordinates.yPos,
                    head: [columnsHead],
                    showHead: false,
                    body: [columnsHeaders],
                    columnStyles: columnStyles,
                    theme: "grid",
                    margin: this.GetBasicMargins(),
                    styles: {
                        ...TableStylesAux,
                        fontStyle: "bold",
                        halign: "center",
                        valign: "middle"
                    }
                })

                this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
            }
            autoTable(this.pdf, {
                startY: this.pdfCoordinates.yPos,
                head: [colsHead],
                styles: {
                    ...TableStylesAux,
                    halign: "center",
                    valign: "middle"
                },
                showHead: false,
                body: subjectQualifs.QualificationsData,
                rowPageBreak: evalType == CTipoEvaluacion.Colores ? "avoid" : "auto",
                didParseCell: (data) => {
                    this.DidParseCellManage(data, evalType);
                },
                didDrawCell: (data) => {
                    this.DidDrawCellManage(data, evalType);
                },
                columnStyles: colsStyles,
                theme: "grid",
                margin: this.GetBasicMargins(),
            })
            this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
        })

        this.pdfCoordinates.yPos += this.padding;
    }

    protected async DrawBoletaContent() {
        await this.GetLogoImage();
        this.headerHeight = this.DrawHeader();

        this.pdfCoordinates.yPos = this.padding + this.headerHeight;
        this.pdfCoordinates.xPos = this.padding;

        this.DrawStudenInfoTbl();
        const positionPostStudentsInfo: number = (this.pdf as any).lastAutoTable.finalY;
        this.DrawSubjectTeachersTbl();
        const positionPostSubjectTeachers: number = (this.pdf as any).lastAutoTable.finalY;

        this.pdfCoordinates.yPos = positionPostStudentsInfo > positionPostSubjectTeachers
            ? positionPostStudentsInfo
            : positionPostSubjectTeachers;
        this.pdfCoordinates.xPos = this.padding;

        this.pdfCoordinates.yPos += this.padding;

        this.DrawQualifications();
        this.AddFixedOrientationPage("portrait");
        this.pdfCoordinates.yPos = this.GetMarginTop();
        this.DrawStaffAssigmentsSignature();

        this.DrawPageNumbers('left');
        this.DrawHeaders();
    }

    protected DrawHeader() {
        const auxPosY = this.pdfCoordinates.yPos;
        const heightLogo = this.DrawLogo();
        this.DrawBoletaTitles();
        const positionPostLogo = this.pdfCoordinates.yPos + heightLogo + this.padding;
        const positionPostTitles = (this.pdf as any).lastAutoTable.finalY + this.padding;

        const positionPostHeader = positionPostLogo > positionPostTitles
            ? positionPostLogo
            : positionPostTitles;

        const headerHeight = positionPostHeader - auxPosY;
        return headerHeight;
    }

    private DrawBoletaTitles() {
        const { Nivel, BoletaCfgTemplate, BoletaActualPosition } = this.config;
        const { FontSizeTitle, FontSizeSubTitle } = this.GetBasicPDFStyles();
        const TableStylesAux = this.GetBasicPDFTableStyles();
        const tableWidth = this.widthArea - (this.GetLogoWidth()) - this.padding;

        let ordinalPeriods = [_L("boleta_v4.primer_trimestre"), _L("boleta_v4.segundo_trimestre"), _L("boleta_v4.tercer_trimestre")];
        const titles = [_L("boleta_v4.performance_evaluation")]

        if (usesInfoHeader) {
            titles.push(
                ordinalPeriods[BoletaActualPosition] || "",
                Nivel || _L("general.nodisponible")
            )
        }

        const indexExtraHeaders = titles.length;
        if (BoletaCfgTemplate?.Extras && BoletaCfgTemplate.Extras.cabeceras_extras && BoletaCfgTemplate.Extras.cabeceras_extras.length) {
            titles.push(...BoletaCfgTemplate.Extras.cabeceras_extras);
        }

        let yPos = this.pdfCoordinates.yPos;

        for (const [index, title] of titles.entries()) {
            autoTable(this.pdf, {
                startY: yPos,
                tableWidth: tableWidth,
                theme: "plain",
                head: [[title]],
                styles: {
                    ...TableStylesAux,
                    halign: "right", valign: "middle", lineWidth: 0, cellPadding: { right: 0 },
                    fontSize: !index ? FontSizeTitle : FontSizeSubTitle,
                    fontStyle: index < indexExtraHeaders ? "bold" : "normal",
                },
                margin: {
                    ...this.GetBasicMargins(),
                    left: this.padding * 2 + this.GetLogoWidth(),
                }
            })
            yPos = (this.pdf as any).lastAutoTable.finalY + this.padding * 0.5;
        }
    }

    private DrawStudenInfoTbl(tableWidth = this.widthArea / 2 - this.padding / 2) {
        const { NombreAlumno, FechaNacimiento, Grado, Grupos, InfoExtra, CicloEscolar } = this.config;
        const StudentInfo: UIUtilViewCalificacion.IInfoExtraBoleta[] = [
            { Tag: `${_L("boleta_v4.alumno")}`, Val: NombreAlumno },
            { Tag: `${_L("boleta_v4.fecha_nacimiento")}`, Val: FechaNacimiento },
            { Tag: `${_L("boleta_v4.grado")}`, Val: Grado },
            { Tag: `${_L("boleta_v4.grupo")}`, Val: Grupos },
        ];

        if (this.usesStudentInfoExtra) {
            let infoExtra = [...InfoExtra];
            if (infoExtra.length) StudentInfo.push(...infoExtra);
        }
        StudentInfo.push({ Tag: _L("boleta_v4.cicloesc"), Val: CicloEscolar });
        let reportCardGralInfo = StudentInfo.map<string[]>(d => [d.Tag + ":", d.Val]);

        !tableWidth && (tableWidth = this.widthArea / 2 - this.padding / 2);

        const TableStylesAux = this.GetBasicPDFTableStyles();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            tableWidth: tableWidth,
            head: [{ "key": "", "val": "" }],
            styles: {
                ...TableStylesAux,
                halign: "left",
                valign: "middle",
                cellPadding: 1
            },
            columnStyles: {
                0: { cellWidth: tableWidth / 10 * 4, fontStyle: 'bold' },
            },
            showHead: false,
            body: reportCardGralInfo,
            theme: "grid",
            margin: this.GetBasicMargins(),
        })
    }

    private DrawSubjectTeachersTbl() {
        const DataSubjectsTeachers: string[][] = UIUtilViewCalificacion._GetSubjectsTeachersStringDataV2(this.config);
        if (!DataSubjectsTeachers.length) return;

        const tableWidth = this.widthArea / 2 - this.padding / 2;
        const TableStylesAux = this.GetBasicPDFTableStyles();

        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            tableWidth: tableWidth,
            head: [[_L("boleta_v4.asignatura"), _L("boleta_v4.teacher")]],
            styles: {
                ...TableStylesAux,
                fontStyle: "normal",
                halign: "left",
                valign: "middle",
                cellPadding: 1
            },
            headStyles: {
                halign: "left", fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: tableWidth / 10 * 5 },
            },
            showHead: true,
            theme: "grid",
            margin: {
                ...this.GetBasicMargins(),
                left: tableWidth + (this.padding * 2)
            },
            body: DataSubjectsTeachers,
        })
    }

    private DrawEvaluationCfg(evalType: CTipoEvaluacion, evalCfg: ItemConfigEval[] = []) {
        this.FixEvaluationCfgPosition(evalCfg, evalType);

        const { FontSizeMedium } = this.GetBasicPDFStyles();
        this.pdfCoordinates.xPos = this.padding;
        this.pdf.setFontSize(FontSizeMedium);
        const evaluationTextDims = this.pdf.getTextDimensions(_L("boleta_v4.evaluacion"));

        this.pdfCoordinates.yPos += evaluationTextDims.h;
        this.pdf.text(_L("boleta_v4.evaluacion"), this.padding, this.pdfCoordinates.yPos);
        this.pdfCoordinates.yPos += this.padding / 2;
        let addedHeight = 0;
        switch (evalType) {
            case CTipoEvaluacion.Colores:
                addedHeight = this.LayoutEvaluationColors(evalCfg, this.pdfCoordinates.xPos, this.pdfCoordinates.yPos, 'draw');
                break;
            case CTipoEvaluacion.Letras:
                addedHeight = this.LayoutEvaluationLetters(evalCfg, this.pdfCoordinates.xPos, this.pdfCoordinates.yPos, 'draw');
                break;
            default:
                break;
        }

        this.pdfCoordinates.yPos += addedHeight;
        this.pdfCoordinates.yPos += this.padding / 3;
    }

    private FixEvaluationCfgPosition(evalCfg: ItemConfigEval[], evalType: CTipoEvaluacion) {
        const { FontSizeMedium } = this.GetBasicPDFStyles();
        let heightEvalCfgContent = 0;
        let posYAux = this.pdfCoordinates.yPos;
        this.pdf.setFontSize(FontSizeMedium);
        let evalTitleTextDims = this.pdf.getTextDimensions(_L("boleta_v4.evaluacion"));

        heightEvalCfgContent += evalTitleTextDims.h;
        heightEvalCfgContent += this.padding / 2;
        switch (evalType) {
            case CTipoEvaluacion.Colores:
                heightEvalCfgContent += this.LayoutEvaluationColors(evalCfg, this.padding, 0, 'calculate');
                break;
            case CTipoEvaluacion.Letras:
                heightEvalCfgContent += this.LayoutEvaluationLetters(evalCfg, this.padding, 0, 'calculate');
                break;
        }

        heightEvalCfgContent += this.padding / 3;

        if ((posYAux + heightEvalCfgContent) > (this.height - this.padding)) {
            this.pdf.addPage();
            this.pdfCoordinates.yPos = this.padding + this.headerHeight;
        }
    }

    private LayoutEvaluationColors(evalCfg: ItemConfigEval[], startX: number, startY: number, mode: 'calculate' | 'draw'): number {
        const { DrawAuxColor } = this.GetBasicPDFStyles();
        if (mode == 'draw') {
            this.pdf.setDrawColor(DrawAuxColor);
        }

        let currentX = startX;
        let currentY = startY;
        let lineHeight = 0;
        let comesFromBreakLine = false;

        for (const [i, evalCfgItem] of evalCfg.entries()) {
            const isLastItem = (i === evalCfg.length - 1);
            const hasDescription = evalCfgItem.Descripcion && evalCfgItem.Descripcion.length;
            const auxTextHeight = this.pdf.getTextDimensions("T").h - 2;
            const textItemDesc = evalCfgItem.Descripcion || "";
            const textItemEqual = hasDescription ? " = " : "";
            const descriptionDims = this.pdf.getTextDimensions(textItemDesc);
            const equalDims = this.pdf.getTextDimensions(textItemEqual);
            const itemCfgWidth = (auxTextHeight * 2) + equalDims.w + descriptionDims.w;

            lineHeight = Math.max(lineHeight, descriptionDims.h);

            if ((currentX + itemCfgWidth) > (this.width - this.padding)) {
                if (i !== 0 && !comesFromBreakLine) {
                    currentY += (this.padding / 3) + this.pdf.getTextDimensions("Tgj").h;
                }
                currentX = this.padding;

                if (itemCfgWidth > this.widthArea) {
                    let textHeight = 0;
                    const splitTextItemCfg = this.pdf.splitTextToSize(textItemDesc, (this.widthArea - (auxTextHeight * 2))) as string[];
                    const totalSplitHeight = splitTextItemCfg.reduce((acc, t, idx) => {
                        const h = this.pdf.getTextDimensions(t).h;
                        return acc + h + (idx == splitTextItemCfg.length - 1 ? 0 : (this.padding / 3 * 0.10));
                    }, 0);

                    const textMiddle = currentY + (totalSplitHeight / 2);
                    if (mode === 'draw') {
                        this.pdf.setFillColor(evalCfgItem.Valor);
                        this.pdf.circle(currentX + auxTextHeight, textMiddle, auxTextHeight, "DF")
                        this.pdf.text(textItemEqual, currentX + (auxTextHeight * 2), textMiddle + (this.pdf.getTextDimensions(textItemEqual).h) / 4);

                        let splitY = currentY;
                        splitTextItemCfg.forEach((t) => {
                            const tHeight = this.pdf.getTextDimensions(t).h;
                            this.pdf.text(t, currentX + (auxTextHeight * 2) + equalDims.w, splitY + splitY + tHeight);
                            splitY += tHeight + (this.padding / 3 * 0.10);
                        });
                    }

                    currentY += totalSplitHeight;
                    if (!isLastItem) {
                        currentY += (this.padding / 3);
                    }
                    currentX = this.padding;
                    lineHeight = 0;
                    comesFromBreakLine = true;
                    continue;
                }
            }

            comesFromBreakLine = false;
            if (mode === 'draw') {
                this.pdf.setFillColor(evalCfgItem.Valor);
                this.pdf.circle(currentX + auxTextHeight, currentY + auxTextHeight, auxTextHeight, "DF");
                this.pdf.text(textItemEqual, currentX + (auxTextHeight * 2), currentY + descriptionDims.h);
                this.pdf.text(textItemDesc, currentX + (auxTextHeight * 2) + equalDims.w, currentY + descriptionDims.h);
            }

            currentX += (itemCfgWidth) + this.padding;
            if (isLastItem) {
                currentY += Math.max(lineHeight, this.pdf.getTextDimensions("Tgj").h);
            }
        }

        return currentY - startY;
    }

    private LayoutEvaluationLetters(evalCfg: ItemConfigEval[], startX: number, startY: number, mode: 'calculate' | 'draw'): number {
        let currentX = startX;
        let currentY = startY;
        let lineHeight = 0;
        let comesFromBreakLine = false;

        for (const [i, evalCfgItem] of evalCfg.entries()) {
            const isLastItem = (i === evalCfg.length - 1);
            const hasDescription = (evalCfgItem.Descripcion && evalCfgItem.Descripcion.length - 1);
            const itemText = `${evalCfgItem.Valor}${hasDescription ? ` = ${evalCfgItem.Descripcion}` : ''}`;
            const itemCfgDim = this.pdf.getTextDimensions(itemText);

            lineHeight = Math.max(lineHeight, itemCfgDim.h);

            // --- Lógica de Salto de Línea ---
            if ((currentX + itemCfgDim.w) > (this.width - this.padding)) {
                if (i !== 0 && !comesFromBreakLine) {
                    currentY += (this.padding / 3) + this.pdf.getTextDimensions("Tgj").h; // Salto de línea
                }
                currentX = this.padding;

                // Manejo de ítem que es más ancho que toda el área
                if (itemCfgDim.w > this.widthArea) {
                    const splitTextItemCfg = this.pdf.splitTextToSize(itemText, this.widthArea) as string[];

                    let splitY = currentY;
                    splitTextItemCfg.forEach((t, idx) => {
                        const isLastLineSplit = (idx === (splitTextItemCfg.length - 1));
                        const tHeight = this.pdf.getTextDimensions(t).h;

                        if (mode === 'draw') {
                            this.pdf.text(t, currentX, splitY + tHeight); // Ajuste de Y para dibujar
                        }

                        splitY += tHeight;
                        if (!isLastLineSplit) {
                            splitY += (this.padding / 3 * 0.10);
                        }
                    });

                    currentY = splitY; // Actualizar Y a la posición final del texto dividido
                    if (!isLastItem) {
                        currentY += (this.padding / 3) + this.pdf.getTextDimensions("Tgj").h;
                    }
                    lineHeight = 0; // Reseteamos
                    comesFromBreakLine = true;
                    continue; // Siguiente ítem
                }
            }

            comesFromBreakLine = false;

            // --- Lógica de Dibujo/Medición (Ítem normal) ---
            if (mode === 'draw') {
                this.pdf.text(itemText, currentX, currentY + lineHeight); // Usar lineHeight para alinear
            }

            currentX += itemCfgDim.w + this.padding;
        }

        // Añadimos la altura de la última línea
        currentY += lineHeight;

        // Devolvemos la altura total consumida
        return currentY - startY;
    }

    private DrawStaffAssigmentsSignature() {
        const { IdEscuela } = this.config;
        const DataSubjectsTeachers: string[][] = [];
        const nameGralDirec = UIUtilViewCalificacion.MapEscuelasDireccionGeneral.get(IdEscuela);
        if (nameGralDirec) {
            DataSubjectsTeachers.push(['Direccion General', nameGralDirec])
        }
        DataSubjectsTeachers.push(...UIUtilViewCalificacion._GetSubjectsTeachersStringDataV2(this.config));
        DataSubjectsTeachers.push(['Sello de la escuela', '']);
        const TableStylesAux = this.GetBasicPDFTableStyles();
        autoTable(this.pdf, {
            startY: this.pdfCoordinates.yPos,
            head: [{ 'period': "Asignatura", 'signature': 'Nombre y Firma' }],
            styles: {
                ...TableStylesAux,
                halign: 'center',
                valign: 'middle',
            },
            columnStyles: {
                0: { cellWidth: this.auxDivWidth * 2 },
                1: { cellWidth: this.auxDivWidth * 4 },
            },
            bodyStyles: {
                halign: 'left', valign: 'top'
            },
            didParseCell: (data: CellHookData) => {
                if (data.section === 'body' && data.row.index === DataSubjectsTeachers.length - 1) {
                    data.row.height = this.auxDivWidth * 1.25;
                }
            },
            showHead: "firstPage",
            theme: 'grid',
            margin: this.GetBasicMargins(),
            body: DataSubjectsTeachers,
        });

        this.pdfCoordinates.yPos = (this.pdf as any).lastAutoTable.finalY;
    }

    private RomanNumConversion(num: number): string {
        // Los números romanos estándar solo llegan hasta el 3999
        if (num < 1 || num > 3999) return String(num);

        const valoresRomanos: { [key: string]: number } = {
            M: 1000,
            CM: 900,
            D: 500,
            CD: 400,
            C: 100,
            XC: 90,
            L: 50,
            XL: 40,
            X: 10,
            IX: 9,
            V: 5,
            IV: 4,
            I: 1
        };

        let resultado = '';

        for (const letra in valoresRomanos) {
            while (num >= valoresRomanos[letra]) {
                resultado += letra;
                num -= valoresRomanos[letra];
            }
        }
        return resultado;
    }

    public async GeneratePDF(): Promise<jsPDF> {
        await this.DrawBoletaContent();
        return this.pdf;
    }
}

export async function _BoletaPDFV4_1(config: IBoletaConfig) {
    const boletaV4 = new BoletaPDFGeneratorV4_1(config);
    return await boletaV4.GeneratePDF();
}